import { useState, useEffect } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { api } from '../../services/api'
import type { HeroContent, ChallengeContent, ScheduleItem, AvisoContent } from '../../services/api'
import { challenges as defaultChallenges, schedule as defaultSchedule, legalBases as defaultLegalBases, defaultAviso } from '../Home'
import { ChevronLeft, Save, Plus, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

const defaultHero: HeroContent = {
  badge: 'Prefeitura Municipal de Porto Velho • Sebrae',
  title: 'Catalisa GOV',
  subtitle: 'Porto Velho',
  description: 'Uma jornada institucional para aproximar a Prefeitura, o Sebrae, o ecossistema de inovação e a sociedade na construção de soluções para desafios públicos municipais.'
}

const inputClass = "w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0d6b3d] focus:border-transparent text-sm"
const labelClass = "block text-sm font-medium text-gray-700 mb-1"

function SaveButton({ saving, onClick }: { saving: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      disabled={saving}
      className="flex items-center gap-2 px-4 py-2 bg-[#0d6b3d] text-white rounded-lg hover:bg-[#0b5b35] transition-colors text-sm font-medium disabled:opacity-60"
    >
      {saving ? (
        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
      ) : (
        <Save size={16} />
      )}
      Salvar
    </button>
  )
}

export default function ContentManager() {
  const { token } = useAuth()
  const [isLoading, setIsLoading] = useState(true)
  const [savingKey, setSavingKey] = useState<string | null>(null)

  const [hero, setHero] = useState<HeroContent>(defaultHero)
  const [challengesState, setChallengesState] = useState<ChallengeContent[]>(
    defaultChallenges.map(({ id, title, question, result }) => ({ id, title, question, result }))
  )
  const [scheduleState, setScheduleState] = useState<ScheduleItem[]>(defaultSchedule)
  const [legalBasesState, setLegalBasesState] = useState<string[]>(defaultLegalBases)
  const [avisoState, setAvisoState] = useState<AvisoContent>(defaultAviso)

  useEffect(() => {
    api.getContent().then((content) => {
      if (content.hero) setHero(content.hero)
      if (content.challenges && content.challenges.length > 0) setChallengesState(content.challenges)
      if (content.schedule && content.schedule.length > 0) setScheduleState(content.schedule)
      if (content.legalBases && content.legalBases.length > 0) setLegalBasesState(content.legalBases)
      if (content.aviso) setAvisoState(content.aviso)
      setIsLoading(false)
    })
  }, [])

  const saveKey = async (key: string, value: unknown) => {
    if (!token) return
    setSavingKey(key)
    try {
      await api.updateContent(token, key, value)
      toast.success('Conteúdo atualizado com sucesso!')
    } catch (error: any) {
      toast.error(error.message || 'Erro ao salvar conteúdo')
    } finally {
      setSavingKey(null)
    }
  }

  const updateChallenge = (id: string, field: keyof ChallengeContent, value: string) => {
    setChallengesState((prev) => prev.map((c) => (c.id === id ? { ...c, [field]: value } : c)))
  }

  const updateScheduleItem = (index: number, field: keyof ScheduleItem, value: string) => {
    setScheduleState((prev) => prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)))
  }

  const addScheduleItem = () => {
    setScheduleState((prev) => [...prev, { date: '', stage: '', note: '' }])
  }

  const removeScheduleItem = (index: number) => {
    setScheduleState((prev) => prev.filter((_, i) => i !== index))
  }

  const updateLegalBase = (index: number, value: string) => {
    setLegalBasesState((prev) => prev.map((item, i) => (i === index ? value : item)))
  }

  const addLegalBase = () => {
    setLegalBasesState((prev) => [...prev, ''])
  }

  const removeLegalBase = (index: number) => {
    setLegalBasesState((prev) => prev.filter((_, i) => i !== index))
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0d6b3d] mx-auto mb-4"></div>
          <p className="text-gray-500">Carregando conteúdo...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-6 py-4 sticky top-0 z-10">
        <div className="flex items-center gap-4">
          <a href="/admin/dashboard" className="text-gray-400 hover:text-gray-600">
            <ChevronLeft size={20} />
          </a>
          <div>
            <h1 className="text-xl font-serif font-bold text-[#0d2d47]">Conteúdo do Site</h1>
            <p className="text-sm text-gray-500">Edite os textos exibidos na página inicial</p>
          </div>
        </div>
      </div>

      <div className="p-6 max-w-4xl mx-auto space-y-6">
        {/* AVISO DE PRORROGAÇÃO */}
        <div className="bg-white rounded-xl border border-[#f1b90f]/40 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif font-bold text-lg text-[#0d2d47]">Aviso de Prorrogação</h2>
            <SaveButton saving={savingKey === 'aviso'} onClick={() => saveKey('aviso', avisoState)} />
          </div>
          <div className="space-y-4">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <input
                type="checkbox"
                checked={avisoState.enabled}
                onChange={(e) => setAvisoState({ ...avisoState, enabled: e.target.checked })}
              />
              Exibir aviso no site
            </label>
            <div>
              <label className={labelClass}>Título</label>
              <input className={inputClass} value={avisoState.title} onChange={(e) => setAvisoState({ ...avisoState, title: e.target.value })} />
            </div>
            <div>
              <label className={labelClass}>Mensagem</label>
              <textarea rows={4} className={inputClass} value={avisoState.message} onChange={(e) => setAvisoState({ ...avisoState, message: e.target.value })} />
            </div>
            <div>
              <label className={labelClass}>Nota de publicação (Diário Oficial)</label>
              <input className={inputClass} value={avisoState.publishNote} onChange={(e) => setAvisoState({ ...avisoState, publishNote: e.target.value })} />
            </div>
          </div>
        </div>

        {/* HERO */}
        <div className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif font-bold text-lg text-[#0d2d47]">Topo da Página (Hero)</h2>
            <SaveButton saving={savingKey === 'hero'} onClick={() => saveKey('hero', hero)} />
          </div>
          <div className="space-y-4">
            <div>
              <label className={labelClass}>Selo/badge</label>
              <input className={inputClass} value={hero.badge || ''} onChange={(e) => setHero({ ...hero, badge: e.target.value })} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Título</label>
                <input className={inputClass} value={hero.title || ''} onChange={(e) => setHero({ ...hero, title: e.target.value })} />
              </div>
              <div>
                <label className={labelClass}>Subtítulo</label>
                <input className={inputClass} value={hero.subtitle || ''} onChange={(e) => setHero({ ...hero, subtitle: e.target.value })} />
              </div>
            </div>
            <div>
              <label className={labelClass}>Descrição</label>
              <textarea rows={3} className={inputClass} value={hero.description || ''} onChange={(e) => setHero({ ...hero, description: e.target.value })} />
            </div>
          </div>
        </div>

        {/* DESAFIOS */}
        <div className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif font-bold text-lg text-[#0d2d47]">Desafios Prioritários</h2>
            <SaveButton saving={savingKey === 'challenges'} onClick={() => saveKey('challenges', challengesState)} />
          </div>
          <div className="space-y-5">
            {challengesState.map((challenge) => (
              <div key={challenge.id} className="border border-gray-100 rounded-lg p-4 bg-gray-50/50">
                <p className="text-xs font-mono font-bold text-[#5d7f48] mb-3">DESAFIO {challenge.id}</p>
                <div className="space-y-3">
                  <div>
                    <label className={labelClass}>Título</label>
                    <input className={inputClass} value={challenge.title} onChange={(e) => updateChallenge(challenge.id, 'title', e.target.value)} />
                  </div>
                  <div>
                    <label className={labelClass}>Pergunta/desafio</label>
                    <textarea rows={2} className={inputClass} value={challenge.question} onChange={(e) => updateChallenge(challenge.id, 'question', e.target.value)} />
                  </div>
                  <div>
                    <label className={labelClass}>Resultado esperado</label>
                    <textarea rows={2} className={inputClass} value={challenge.result} onChange={(e) => updateChallenge(challenge.id, 'result', e.target.value)} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CRONOGRAMA */}
        <div className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif font-bold text-lg text-[#0d2d47]">Cronograma Indicativo</h2>
            <SaveButton saving={savingKey === 'schedule'} onClick={() => saveKey('schedule', scheduleState)} />
          </div>
          <div className="space-y-3">
            {scheduleState.map((item, index) => (
              <div key={index} className="border border-gray-100 rounded-lg p-4 bg-gray-50/50 flex gap-3">
                <div className="flex-1 space-y-2">
                  <input className={inputClass} placeholder="Data" value={item.date} onChange={(e) => updateScheduleItem(index, 'date', e.target.value)} />
                  <input className={inputClass} placeholder="Etapa" value={item.stage} onChange={(e) => updateScheduleItem(index, 'stage', e.target.value)} />
                  <input className={inputClass} placeholder="Observação" value={item.note} onChange={(e) => updateScheduleItem(index, 'note', e.target.value)} />
                </div>
                <button onClick={() => removeScheduleItem(index)} className="p-2 h-fit text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Remover">
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
          <button onClick={addScheduleItem} className="mt-3 flex items-center gap-2 text-sm text-[#0d6b3d] hover:underline">
            <Plus size={14} /> Adicionar item
          </button>
        </div>

        {/* BASE LEGAL */}
        <div className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif font-bold text-lg text-[#0d2d47]">Base Legal</h2>
            <SaveButton saving={savingKey === 'legalBases'} onClick={() => saveKey('legalBases', legalBasesState)} />
          </div>
          <div className="space-y-2">
            {legalBasesState.map((base, index) => (
              <div key={index} className="flex gap-2">
                <input className={inputClass} value={base} onChange={(e) => updateLegalBase(index, e.target.value)} />
                <button onClick={() => removeLegalBase(index)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Remover">
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
          <button onClick={addLegalBase} className="mt-3 flex items-center gap-2 text-sm text-[#0d6b3d] hover:underline">
            <Plus size={14} /> Adicionar item
          </button>
        </div>
      </div>
    </div>
  )
}
